import { useCallback, useEffect, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Link from '@tiptap/extension-link';
import { Table, TableRow, TableHeader, TableCell } from '@tiptap/extension-table';
import { VariableExtension } from './variable-tiptap-extension';
import { WysiwygToolbar } from './WysiwygToolbar';
import { markdownToHtml, htmlToMarkdown } from './markdown-utils';

export interface MarkdownEditorProps {
    /** The markdown content with {{variables}} */
    value: string;
    /** Called when content changes */
    onChange?: (value: string) => void;
    /** Whether the editor is read-only */
    readOnly?: boolean;
    /** Sample data to show for variables in readonly mode */
    sampleData?: Record<string, string>;
    /** Minimum height of the editor */
    minHeight?: string;
    /** Placeholder text when empty */
    placeholder?: string;
    /** Called when a variable is dropped onto the editor */
    onVariableDrop?: (variableName: string, position: number) => void;
    /** Custom class name for the container */
    className?: string;
    /** Whether to show the formatting toolbar */
    showToolbar?: boolean;
}

/**
 * MarkdownEditor - A TipTap-based WYSIWYG editor with support for:
 * - Rich text formatting (headings, bold, italic, lists, etc.)
 * - Formatting toolbar
 * - Interactive variable chips ({{variableName}})
 * - Drag-and-drop variable insertion
 * - Click-to-edit variable names
 * - Readonly mode with sample data display
 */
export function MarkdownEditor({
    value,
    onChange,
    readOnly = false,
    sampleData,
    minHeight = '300px',
    placeholder = 'Enter prompt content...',
    onVariableDrop,
    className,
    showToolbar = true,
}: MarkdownEditorProps) {
    const initialContent = markdownToHtml(value);
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;
    const lastEmitted = useRef(value);

    const editor = useEditor({
        immediatelyRender: false,
        extensions: [
            StarterKit.configure({
                heading: { levels: [1, 2, 3] },
            }),
            Placeholder.configure({ placeholder }),
            Link.configure({ openOnClick: false }),
            Table.configure({
                resizable: false,
                HTMLAttributes: { class: 'tiptap-table' },
            }),
            TableRow,
            TableHeader,
            TableCell,
            VariableExtension.configure({
                sampleData,
                readOnly,
                onDelete: () => {},
                onRename: () => {},
            }),
        ],
        content: initialContent,
        editable: !readOnly,
    });

    useEffect(() => {
        if (!editor || readOnly) return;

        const handler = () => {
            if (!editor.isFocused || !onChangeRef.current) return;
            const md = htmlToMarkdown(editor.getHTML());
            lastEmitted.current = md;
            onChangeRef.current(md);
        };

        editor.on('update', handler);
        return () => { editor.off('update', handler); };
    }, [editor, readOnly]);

    useEffect(() => {
        if (!editor) return;
        if (value === lastEmitted.current) return;
        lastEmitted.current = value;
        editor.commands.setContent(markdownToHtml(value), { emitUpdate: false });
    }, [editor, value]);

    useEffect(() => {
        if (editor) {
            editor.setEditable(!readOnly);
        }
    }, [editor, readOnly]);

    // Handle drag over
    const handleDragOver = useCallback((e: React.DragEvent) => {
        if (readOnly) return;
        
        const hasVariableData = e.dataTransfer.types.includes('application/x-variable');
        if (hasVariableData) {
            e.preventDefault();
            e.dataTransfer.dropEffect = 'copy';
        }
    }, [readOnly]);

    // Handle drop
    const handleDrop = useCallback(
        (e: React.DragEvent) => {
            if (readOnly || !editor) return;

            const variableData = e.dataTransfer.getData('application/x-variable');
            if (!variableData) return;

            e.preventDefault();

            try {
                const { name } = JSON.parse(variableData);
                if (!name) return;

                // Get position from coordinates
                const pos = editor.view.posAtCoords({ left: e.clientX, top: e.clientY });
                if (pos) {
                    // Insert variable node at position
                    editor
                        .chain()
                        .focus()
                        .insertContentAt(pos.pos, {
                            type: 'variable',
                            attrs: { name },
                        })
                        .run();
                    
                    onVariableDrop?.(name, pos.pos);
                }
            } catch {
                // Invalid JSON, ignore
            }
        },
        [readOnly, editor, onVariableDrop]
    );

    if (!editor) {
        return null;
    }

    return (
        <div
            className={`
                markdown-editor-root
                rounded-lg border border-slate-200 bg-white overflow-hidden
                ${readOnly ? 'bg-slate-50' : 'nodrag nopan nowheel focus-within:border-amber-400 focus-within:ring-1 focus-within:ring-amber-400'}
                ${className || ''}
            `}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
        >
            <style>{`
                .markdown-editor-root .ProseMirror {
                    outline: none;
                }
                .markdown-editor-root .ProseMirror > * + * {
                    margin-top: 0.75em;
                }
                .markdown-editor-root .ProseMirror > h1,
                .markdown-editor-root .ProseMirror > h2,
                .markdown-editor-root .ProseMirror > h3 {
                    margin-top: 1.4em;
                    margin-bottom: 0.3em;
                }
                .markdown-editor-root .ProseMirror > *:first-child {
                    margin-top: 0;
                }
                .markdown-editor-root .ProseMirror p.is-editor-empty:first-child::before {
                    color: #94a3b8;
                    content: attr(data-placeholder);
                    float: left;
                    height: 0;
                    pointer-events: none;
                }

                /* Headings */
                .markdown-editor-root .ProseMirror h1 {
                    font-size: 1.75em;
                    font-weight: 700;
                    line-height: 1.3;
                    color: #0f172a;
                }
                .markdown-editor-root .ProseMirror h2 {
                    font-size: 1.35em;
                    font-weight: 600;
                    line-height: 1.35;
                    color: #1e293b;
                }
                .markdown-editor-root .ProseMirror h3 {
                    font-size: 1.15em;
                    font-weight: 600;
                    line-height: 1.4;
                    color: #334155;
                }

                /* Inline code */
                .markdown-editor-root .ProseMirror code {
                    background-color: #f1f5f9;
                    border: 1px solid #e2e8f0;
                    border-radius: 4px;
                    padding: 1px 5px;
                    font-family: ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace;
                    font-size: 0.875em;
                    color: #be185d;
                }

                /* Fenced code blocks */
                .markdown-editor-root .ProseMirror pre {
                    background-color: #1e293b;
                    border-radius: 8px;
                    padding: 14px 18px;
                    overflow-x: auto;
                    margin: 0.75em 0;
                }
                .markdown-editor-root .ProseMirror pre code {
                    background: none;
                    border: none;
                    border-radius: 0;
                    padding: 0;
                    color: #e2e8f0;
                    font-size: 0.85em;
                    line-height: 1.6;
                }

                /* Blockquotes */
                .markdown-editor-root .ProseMirror blockquote {
                    border-left: 3px solid #d97706;
                    padding-left: 1em;
                    margin-left: 0;
                    color: #475569;
                    font-style: italic;
                }

                /* Lists */
                .markdown-editor-root .ProseMirror ul {
                    list-style-type: disc;
                    padding-left: 1.5em;
                }
                .markdown-editor-root .ProseMirror ol {
                    list-style-type: decimal;
                    padding-left: 1.5em;
                }
                .markdown-editor-root .ProseMirror li {
                    margin-top: 0.25em;
                }
                .markdown-editor-root .ProseMirror li > p {
                    margin: 0;
                }

                /* Strong / Em */
                .markdown-editor-root .ProseMirror strong {
                    font-weight: 600;
                    color: #0f172a;
                }

                /* Links */
                .markdown-editor-root .ProseMirror a {
                    color: #2563eb;
                    text-decoration: underline;
                    text-underline-offset: 2px;
                }

                /* Horizontal rule */
                .markdown-editor-root .ProseMirror hr {
                    border: none;
                    border-top: 1px solid #e2e8f0;
                    margin: 1.5em 0;
                }

                /* Tables */
                .markdown-editor-root .ProseMirror table {
                    border-collapse: collapse;
                    width: 100%;
                    margin: 0.75em 0;
                    font-size: 0.9em;
                }
                .markdown-editor-root .ProseMirror table th,
                .markdown-editor-root .ProseMirror table td {
                    border: 1px solid #cbd5e1;
                    padding: 6px 12px;
                    text-align: left;
                    vertical-align: top;
                }
                .markdown-editor-root .ProseMirror table th {
                    background-color: #f1f5f9;
                    font-weight: 600;
                    color: #334155;
                }
                .markdown-editor-root .ProseMirror table td {
                    color: #475569;
                }
                .markdown-editor-root .ProseMirror table tr:hover td {
                    background-color: #f8fafc;
                }
            `}</style>

            {/* Formatting Toolbar */}
            {showToolbar && !readOnly && (
                <WysiwygToolbar editor={editor} />
            )}

            {/* Editor */}
            <EditorContent
                editor={editor}
                className="p-4 text-base leading-relaxed text-slate-700"
                style={{ minHeight }}
            />
        </div>
    );
}
